import { Metrics } from "../analytics/Metrics";
import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableContainer from "../core/ResizableContainer";
import ScrollingImage from "../core/ScrollingImage";
import Decoration from "../game/Decoration";
import DecorationButton from "../game/DecorationButton";
import { DecorationData, DecorationList } from "../game/DecorationData";
import Scrollbar from "../game/Scrollbar";
import ScrollRect from "../game/ScrollRect";
import GameColors from "../GameColors";
import { GameScene } from "../scenes/GameScene";
import Fonts from "../utils/Fonts";
import LayoutUtils from "../utils/LayoutUtils";
import CategoryButton from "./CategoryButton";

export type DecorationCategory = "accessories" | "filters" | "frames" | "dolls" | "stickers";

export default class DecorationMenu extends ResizableContainer {
	public static readonly ON_DECO_SPAWN = "onDecoSpawn";

	scene: GameScene;

	protected decorations: DecorationList;
	protected currentCategory: DecorationCategory;
	protected activeDolls: string[] = [];

	protected buttonContainer: Phaser.GameObjects.Container;
	decoButtons: DecorationButton[] = [];
	categoryButtons: CategoryButton[] = [];

	scrollRect: ScrollRect;
	protected scrollBar: Scrollbar;
	maskImg: Phaser.GameObjects.Image;
	scrollInner: Phaser.GameObjects.Image;

	constructor(scene: GameScene) {
		super(scene, 0, 0);

		this.decorations = scene.cache.json.get("decorations") as DecorationList;

		const categoryContainer = scene.add.container();
		this.add(categoryContainer);
		const categories: DecorationCategory[] = ["dolls", "stickers", "frames", "accessories", "filters"];
		for (const cat of categories) {
			const button = new CategoryButton(scene, cat, (category): void => {
				if (this.currentCategory == category) return;
				this.categoryButtons.forEach((b) => b.enableButton(b.category != category));
				this.setCategory(category);
			});
			button.enableButton(cat != "dolls");
			this.categoryButtons.push(button);
			categoryContainer.add(button);
		}
		const size = new Phaser.Math.Vector2(this.categoryButtons[0].width, this.categoryButtons[0].height - 2);
		LayoutUtils.alignGrid(scene, this.categoryButtons, categoryContainer, size, 1, 30);
		categoryContainer.setSize(size.x, size.y * this.categoryButtons.length);

		const scrollRectContainer = scene.add.container();
		this.add(scrollRectContainer);
		const borderHeight = 10;
		const scrollRectBG = scene.add.image(0, 0, "sideBacker");
		scrollRectContainer.add(scrollRectBG);
		scrollRectContainer.setSize(scrollRectBG.displayWidth, scrollRectBG.displayHeight);

		const divider = scene.add.image(0, 0, "divider");
		scrollRectContainer.add(divider);

		const scrollInner = this.scrollInner = scene.add.image(0, 0, "sideInner");
		scrollRectContainer.add(scrollInner);
		this.scene.alignIn.Center(scrollInner, scrollRectBG, -5);

		const width = scrollRectContainer.width + categoryContainer.width - 3;
		this.setSize(width, scrollRectContainer.height);

		scrollRectContainer.x = (this.width - scrollRectContainer.width) / 2;
		this.scene.alignTo.LeftCenter(categoryContainer, scrollRectContainer, -3, 2);
		scene.alignIn.RightCenter(divider, scrollRectBG, 42, -20);
		
		const scrollViewport = scene.add.container();
		scrollViewport.setSize(scrollInner.displayWidth, scrollInner.displayHeight - borderHeight);
		scrollRectContainer.add(scrollViewport);

		this.currentCategory = "accessories";
		let longestList = this.decorations.accessories;
		for (const key of Object.keys(this.decorations)) {
			const list = (<Record<string, DecorationData[]>>this.decorations)[key];
			// console.log(list);
			if (list && list.length > longestList.length) longestList = list;
			// console.log(longestList);
		}

		const buttonContainer = this.buttonContainer = scene.add.container();
		scrollRectContainer.add(buttonContainer);
		this.decoButtons = [];
		for(let i = 0; i < longestList.length; i++){
			const isEven = i % 2 == 0;
			const button = new DecorationButton(scene, longestList[i], isEven ? "base1" : "base2", i);
			buttonContainer.add(button);
			this.decoButtons.push(button);
		}
		this.scrollBar = new Scrollbar(scene, scrollInner.displayHeight - borderHeight);
		scrollRectContainer.add(this.scrollBar);
		this.scrollRect = new ScrollRect(scene, buttonContainer, scrollViewport, this.scrollBar);
		for (const button of this.decoButtons) {
			button.on(DecorationButton.ON_DRAG_VERTICAL, (ptr: Phaser.Input.Pointer) => {
				this.scrollRect.onPointerDown(ptr);
			});
		}
		// scrollViewport.removeInteractive();
		this.scrollBar.setScrollRect(this.scrollRect);
		this.scrollRect.setProgress(0);
		this.scene.alignIn.RightCenter(this.scrollBar, scrollInner, 13, 0);

		this.setCategory("dolls");

		const maskImg = this.maskImg = scene.add.image(0, 0, "scrollInner");
		maskImg.setSize(scrollInner.displayWidth, scrollInner.displayHeight - borderHeight);
		maskImg.setDisplaySize(scrollInner.displayWidth, scrollInner.displayHeight - 6);
		maskImg.setVisible(false);

		const mask = maskImg.createBitmapMask();
		maskImg.once(Phaser.GameObjects.Events.DESTROY, () => mask.destroy());
		buttonContainer.setMask(mask);

		// const gradient = scene.add.image( 0, 0, "sideGradient");
		// this.add(gradient);
		// this.scene.alignIn.Center(gradient, scrollInner, 61);

		this.addResize(() => {
			this.alignButtons();
			const transform = scrollInner.getWorldTransformMatrix();
			maskImg.setPosition(transform.tx, transform.ty);
		});
	}

	selectCurrentDecoButton(index: number): void{
		for(const button of this.decoButtons){
			if (button.index != index) {
				button.setScale(1);
				button.setButtonTexture(button.baseImage);
			} else button.setButtonTexture(`${button.baseImage}Select`);
		}
	}

	resetCurrentDecoButton(): void {
		for(const button of this.decoButtons) {
			button.setScale(1);
			button.setButtonTexture(button.baseImage);
		}
	}

	setCategory(category?: DecorationCategory): void {
		this.currentCategory = category ?? this.currentCategory;
		this.resetCurrentDecoButton();
		const cat = category.charAt(0).toUpperCase() + category.slice(1)
		if(this.scene.headerText){
			this.scene.headerText.text = Strings.get(cat);
			this.scene.alignHeader();
		}
		const dolls = this.currentCategory == "dolls";
		const decoList = (<Record<string, DecorationData[]>>this.decorations)[this.currentCategory] ?? [];
		for (let i = 0; i < this.decoButtons.length; i++) {
			const button = this.decoButtons[i];
			if (i >= decoList.length || decoList[i].disable == true) {
				button.setVisible(false);
				continue;
			}
			button.setDecoration(decoList[i]);
			button.setVisible(true);
			if (dolls) {
				button.lockButton(this.activeDolls.includes(decoList[i].id));
			} else button.lockButton(false);

			switch (this.currentCategory) {
				case "filters": {
					const decoration = decoList[i];
					const filter = decoration.id == "no-filter" ? undefined : decoration.id;
					if (this.scene.photo.getFilter() == filter) {
						this.selectCurrentDecoButton(button.index);
						button.setScale(1.1);
					}
					button.setCallback(() =>{
						this.scene.soundManager.play("click");
						if (this.scene.photo.setFilter(filter)) {
							this.selectCurrentDecoButton(button.index);
							if (decoration) {
								const itemName = decoration.name ?? decoration.id;
								Metrics.analytics.logEvent("select_item", { game: "ag_photobooth", category: "filters", item: itemName });
							}
						}
						else this.selectCurrentDecoButton(-1);
					} );
					break;
				}
				case "frames": {
					const decoration = decoList[i];
					let frame = decoration.id ?? "no-frame";
					if (this.scene.photo.getFrame() == frame) {
						this.selectCurrentDecoButton(button.index);
						button.setScale(1.1);
					}
					button.setCallback(() => {
						this.scene.soundManager.play("click");
						if (this.scene.photo.setFrame(frame)) {
							this.selectCurrentDecoButton(button.index);
							if (decoration) {
								const itemName = decoration.name ?? decoration.id;
								Metrics.analytics.logEvent("select_item", { game: "ag_photobooth", category: "frames", item: itemName });
							}
						}
						else this.selectCurrentDecoButton(-1);
					});
					break;
				}
				case "dolls":
					button.setCallback((ptr, drag) => {
						this.spawnDoll(button, ptr, drag);
						this.selectCurrentDecoButton(button.index);
					});
					break;
				default:
					button.setCallback((ptr, drag) => {
						this.spawnDecoration(button, ptr, drag);
						this.selectCurrentDecoButton(button.index);
					});
					break;
			}

			if (this.currentCategory == "filters" || this.currentCategory == "frames") {
				button.horizontalEnabled = false;
			} else {
				button.horizontalEnabled = true;
			}
			button.enableDragging(true);
		}
		this.alignButtons();
		this.scrollRect.setProgress(0);
	}

	spawnDoll(button: DecorationButton, ptr: Phaser.Input.Pointer, dragged: boolean): void {
		button.lockButton(true);
		const id = button.decoData.id;
		this.activeDolls.push(id);
		const decoration = this.spawnDecoration(button, ptr, dragged, true);
		// We need to unlock the doll button
		decoration.once(Phaser.GameObjects.Events.DESTROY, () => {
			const index = this.activeDolls.indexOf(id);
			if (index < this.activeDolls.length) this.activeDolls.splice(index, 1);
			// Unlock button if it's currently active
			if (this.currentCategory == "dolls") {
				const button = this.decoButtons.find((b) => b.decoData.id == id);
				if(button && button.scene) button.lockButton(false);
			}
		});
	}

	spawnDecoration(button: DecorationButton, ptr: Phaser.Input.Pointer, dragged: boolean, masked = false): Decoration {
		const pos = new Phaser.Math.Vector2(ptr.worldX, ptr.worldY);
		const decoration = new Decoration(this.scene, pos.x, pos.y, button.decoData, this.currentCategory);
		decoration.masked = masked;
		if (dragged) {
			decoration.startDrag(ptr, true);
			this.scene.soundManager.play("itemDrag");
		} else {
			this.scene.alignIn.Center(decoration, this.scene.photo);
			this.scene.soundManager.play("click");
		}
		this.emit(DecorationMenu.ON_DECO_SPAWN, decoration);
		return decoration;
	}

	update(dt: number): void {
		this.scrollRect?.update(dt);
	}

	protected alignButtons(): void {
		if (!this.scene) return;

		const activeButtons = this.decoButtons.filter((b) => b.visible);
		const size = new Phaser.Math.Vector2(this.decoButtons[0].width, this.decoButtons[0].height + 10);
		this.buttonContainer.setSize(size.x, size.y * activeButtons.length + 20);
				
        const buttonsHeight = size.y * activeButtons.length;
        for (let i = 0; i < activeButtons.length; i++) {
            const button = activeButtons[i];
            const y = i;
            const yPos = (y / activeButtons.length) * buttonsHeight - 7;
			const xPos = button.x - 4;
			button.x = -10;
			button.y = yPos - buttonsHeight / 2 + size.y / 2;
        }

		const progress = Phaser.Math.Clamp(this.scrollRect.getProgress(), 0, 1);
		this.scrollRect.calculateBounds();
		this.scrollRect.setProgress(progress);
		if (this.buttonContainer.height <= this.scrollRect.viewport.height) {
			this.scrollBar.setVisible(false);
			this.buttonContainer.y = 0;
		} else this.scrollBar.setVisible(true);
	}
}